import { useState } from 'react';
import clsx from 'classnames';
import { mixpanelTrack } from '@providers/Mixpanel';
import FileZip from '@components/Icons/FileZip';
import { getUrlBase } from '@helpers/urlsHelper';
import ProgressIndicator from '@/components/ProgressIndicator';
import { useToast, useUser } from '@/hooks';
import { logError } from '@/providers/ErrorTracking';
import simpleDebug from '@/helpers/simpleDebug';
import s from './DownloadArchive.module.scss';

type Props = {
  accessToken?: string;
  className?: string;
  userWorkflow: { id: string };
  documents: { id: string }[];
};
const DownloadZip: React.FC<Props> = ({ accessToken, className, documents, userWorkflow }) => {
  const [user] = useUser();
  const authToken = user?.token;
  const addToast = useToast();
  const [isZIPLoading, setIsZIPLoading] = useState(false);

  if (documents.length === 0) {
    return null;
  }

  const zipDownloadLink = getUrlBase('/documents-proxy/documents/archive.zip', {
    accessToken,
    archiveName: `userWorkflow-${userWorkflow.id}-documents`,
    documentId: documents.map((d) => d.id),
  });

  const handleZIPClick = (): void => {
    if (authToken) {
      mixpanelTrack('Workflow - Download archive as ZIP', { userWorkflowId: userWorkflow.id });
      setIsZIPLoading(true);
      simpleDebug('downloadArchive', 'zip start');

      const xhr = new XMLHttpRequest();
      xhr.open('GET', zipDownloadLink, true);

      xhr.onload = () => {
        simpleDebug('downloadArchive', 'zip done');
        addToast(useToast.ToastTypeVariants.SUCCESS, 'Downloaded files as ZIP');
        setIsZIPLoading(false);
      };

      xhr.onerror = (error) => {
        simpleDebug('downloadArchive', 'zip error', error);
        logError(error, {
          flow: 'workflows',
          userWorkflowId: userWorkflow.id,
        });
        addToast(useToast.ToastTypeVariants.ERROR, 'Failed to download files as ZIP');
        setIsZIPLoading(false);
      };

      xhr.setRequestHeader('Authorization', authToken);

      xhr.send();
    }
  };

  return (
    <a
      className={clsx(s['download-link'], className)}
      download
      href={zipDownloadLink}
      data-cy="downloadZip"
      onClick={handleZIPClick}
      style={{ opacity: isZIPLoading ? 0.6 : 1, pointerEvents: isZIPLoading ? 'none' : 'auto' }}
    >
      <FileZip /> Download all as a ZIP
      {isZIPLoading && <ProgressIndicator />}
    </a>
  );
};

export default DownloadZip;
